import {
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "components/NoScrollDialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DropdownSelect from "components/DropdownSelect";
import IndeterminateIcon from "components/IndeterminateIcon";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useLanguage } from "contexts/language.context";
import * as React from "react";
import toast from "react-hot-toast";
import accountsService from "services/accounts-service";
import useObjectState from "utils/useObjectState";

export default function RolesDialog({
  isOpen,
  action,
  data,
  onSubmit,
  accountId,
}) {
  const { label } = useLanguage();
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [role, setRole] = React.useState({
    description: "",
    permissions: [],
  });

  const [options, changeOptions] = useObjectState({
    accounts: null,
  });

  const [permissions, setPermissions] = React.useState([]);

  const handleGetAccounts = async () => {
    const [response, data] = await accountsService.getAccounts({ raw: true });
    if (response.ok) {
      changeOptions("accounts", data);
    } else {
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
  }, [isOpen]);

  React.useEffect(() => {
    if (action == "create") {
      setRole(data?.role);
      setPermissions(data?.permissions);
    } else {
      setRole(data?.role);
      setPermissions(data?.permissions);
    }
  }, [data]);

  React.useEffect(() => {
    handleGetAccounts();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const dropdownOptions = React.useMemo(() => {
    return {
      accounts:
        options.accounts?.map((account) => ({
          id: account.id,
          label: account.name,
        })) || [],
    };
  }, [options]);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      PaperProps={{
        component: "form",
        onSubmit: async (event) => {
          event.preventDefault();

          setLoading(true);
          const result = await onSubmit(role);
          if (result.success) {
            handleClose();
          } else {
          }

          setLoading(false);
        },
      }}
    >
      <DialogTitle>
        {action == "edit" ? label("edit-role") : label("create-role")}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <MDInput
              autoFocus
              required
              margin="dense"
              id="role-input"
              name="r"
              label={label("name-singular")}
              type="text"
              value={role?.description}
              onChange={(e) => {
                setRole({ ...role, description: e.target.value });
              }}
              fullWidth
            />
          </Grid>{" "}
          {!accountId && (
            <Grid item xs={12}>
              <DropdownSelect
                required
                label={label("account-singular")}
                initialValue={role?.accountId}
                onChange={(e) => {
                  setRole({ ...role, accountId: e?.id });
                }}
                items={dropdownOptions.accounts}
                fullWidth
                renderOption={(option) => (
                  <MDBox
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      sx={{ height: "30px", width: "30px" }}
                      src={option.url}
                    />
                    <MDTypography variant="body" sx={{ fontWeight: "400" }}>
                      {option.label}
                    </MDTypography>
                  </MDBox>
                )}
              />
            </Grid>
          )}
          <Grid item xs={12} container spacing={1}>
            <Grid item container spacing={1}>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        role?.permissions?.length === permissions?.length
                      }
                      indeterminate={
                        role?.permissions?.length !== permissions?.length &&
                        role?.permissions?.length !== 0
                      }
                      indeterminateIcon={<IndeterminateIcon />}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRole({ ...role, permissions });
                        } else {
                          setRole({ ...role, permissions: [] });
                        }
                      }}
                    />
                  }
                  label={label("select-all")}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              {
                <PermissionsChecker
                  allPermissions={permissions}
                  permissions={role?.permissions}
                  onChange={(newPermissions) => {
                    setRole({ ...role, permissions: newPermissions });
                  }}
                />
              }
              {/* <DropdownSelect
                label={label("permission-plural")}
                id="permissions-select"
                initialValue={
                  role?.permissions?.map((permission) => permission.id) || []
                }
                items={
                  permissions?.map((permission, index) => {
                    return { id: permission.id, label: permission.description };
                  }) || []
                }
                onChange={(newPermissions) => {
                  setRole({ ...role, permissions: newPermissions });
                  //setSelectedPermission([...newPermissions]);
                }}
                disableCloseOnSelect
                multiple
              /> */}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{label("cancel-button")}</Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="gradient"
          color="info"
        >
          {action == "edit" ? label("save-button") : label("create-button")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function PermissionsChecker({ allPermissions, permissions, onChange }) {
  const { label } = useLanguage();

  const grouped = React.useMemo(() => {
    const grouped = {};
    allPermissions.forEach((permission) => {
      if (!grouped[permission.entity]) {
        grouped[permission.entity] = [];
      }

      grouped[permission.entity].push(permission);
    });

    return grouped;
  }, [allPermissions]);

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      {Object.keys(grouped).map((entity) => {
        return (
          <Grid
            key={"Role Entity: " + entity}
            item
            xs={4}
            sx={{
              height: "100%",
              p: 2,
            }}
          >
            <FormControlLabel
              label={label(entity)}
              control={
                <Checkbox
                  checked={grouped[entity].every((v) =>
                    permissions.find((p) => p.id == v.id)
                  )}
                  indeterminate={
                    !grouped[entity].every((v) =>
                      permissions.find((p) => p.id == v.id)
                    ) &&
                    grouped[entity].some((v) =>
                      permissions.find((p) => p.id == v.id)
                    )
                  }
                  indeterminateIcon={<IndeterminateIcon />}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChange([
                        ...permissions.filter(
                          (permission) =>
                            !grouped[entity].find((p) => p.id == permission.id)
                        ),
                        ...grouped[entity],
                      ]);
                    } else {
                      onChange(
                        permissions.filter(
                          (permission) =>
                            !grouped[entity].find((p) => p.id == permission.id)
                        )
                      );
                    }
                  }}
                  sx={{ p: 0.5, m: 0 }}
                />
              }
            />
            <Box
              sx={{
                pl: 3,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -2,
                  left: "2px",
                  height: "calc(100% - 9px)",
                  width: "1px",
                  bgcolor: "divider",
                },
              }}
            >
              {grouped[entity].map((permission) => {
                return (
                  <Box
                    key={
                      "Role Entity: " +
                      entity +
                      " - Permission: " +
                      permission.id
                    }
                    sx={{
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "14px",
                        left: "-21px",
                        height: "1px",
                        width: "17px",
                        bgcolor: "divider",
                      },
                    }}
                  >
                    <FormControlLabel
                      sx={{ p: 0, m: 0 }}
                      label={permission.description}
                      control={
                        <Checkbox
                          sx={{ p: 0, m: 0 }}
                          checked={Boolean(
                            permissions?.find((p) => p.id == permission.id)
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              onChange([...permissions, permission]);
                            } else {
                              onChange(
                                permissions.filter((p) => p.id != permission.id)
                              );
                            }
                          }}
                        />
                      }
                    />
                  </Box>
                );
              })}
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
